import { createEntityAdapter, createSelector, createSlice, EntityState, PayloadAction } from "@reduxjs/toolkit";
import { BodyMask, ContourAttachSeries, FillHoles, HoleMask, ImageContourGeneration, ImageCustomizationEntities, ImageCustomizationOutput, ImageCustomizationOutputEntities, ImageDicomRestriction, ImageDicomTag, ImageOutputGeometry, ImagePostProcessing, KeepLargestComponent } from "./image-types";
import { AeTitleRule, CustomizationBase, CustomizationObjectType, DicomAttributeRule, DicomRule, METADATA_FILENAME_ATTRIBUTE, Modality, ModalityDirection, Model, ModelVisibility, OutputMetadataItem, PhysicalProperty } from "../global-types/customization-types";
import { BackendValidationErrorViewModel } from "../global-types/store-errors";
import { composeTriggerValidationErrors } from "../global-types/validation/selectors";
import { has, isObject } from "lodash-es";
import { naturalSort } from "../../util/sort";
import { ImageSliceWrapper } from "../global-types/modelTypeReducerHelper/imageSliceWrapper";
import { DuplicatedIdMap, setObjectAndAncestorsAsModified, updateModelTimestampsAfterSave } from "../global-types/reducer-helpers";
import { createNewImageDicomTag } from "./image-helpers";
import { LengthValue } from "../global-types/units";
import { MVisionAppClient } from "../configurationTarget/mvision-client-list";
import { BackendValidationError, ConcurrentSaveError } from "../../util/errors";
import { assertIsDefined } from "../../util/assert";
import { literalTextToRegex } from "../../util/string-convert";

// entity adapters (see https://redux-toolkit.js.org/api/createEntityAdapter) for image customization types
export const modelAdapter = createEntityAdapter<Model>();
export const customizationBaseAdapter = createEntityAdapter<CustomizationBase>();
export const imageOutputAdapter = createEntityAdapter<ImageCustomizationOutput>();
export const outputMetadataAdapter = createEntityAdapter<OutputMetadataItem>();

export const imageDicomRestrictionAdapter = createEntityAdapter<ImageDicomRestriction>();
export const imageOutputGeometryAdapter = createEntityAdapter<ImageOutputGeometry>();
export const imageContourGenerationAdapter = createEntityAdapter<ImageContourGeneration>();
export const imagePostProcessingAdapter = createEntityAdapter<ImagePostProcessing>();
export const imageDicomTagAdapter = createEntityAdapter<ImageDicomTag>();
export const keepLargestComponentAdapter = createEntityAdapter<KeepLargestComponent>();
export const fillHolesAdapter = createEntityAdapter<FillHoles>();
export const bodyMaskAdapter = createEntityAdapter<BodyMask>();
export const holeMaskAdapter = createEntityAdapter<HoleMask>();

export const aeTitleRuleAdapter = createEntityAdapter<AeTitleRule>();
export const dicomRuleAdapter = createEntityAdapter<DicomRule>();
export const dicomAttributeRuleAdapter = createEntityAdapter<DicomAttributeRule>();

// entity adapter selectors -- these should not be exported/used directly but rather through the slice selectors
export const modelAdapterSelectors = modelAdapter.getSelectors((state: ImageSliceState) => state.models);
export const customizationBaseAdapterSelectors = customizationBaseAdapter.getSelectors((state: ImageSliceState) => state.customizationBases);
export const imageOutputAdapterSelectors = imageOutputAdapter.getSelectors((state: ImageSliceState) => state.imageOutputs);
export const outputMetadataAdapterSelectors = outputMetadataAdapter.getSelectors((state: ImageSliceState) => state.outputMetadata);

export const imageDicomRestrictionAdapterSelectors = imageDicomRestrictionAdapter.getSelectors((state: ImageSliceState) => state.imageDicomRestrictions);
export const imageOutputGeometryAdapterSelectors = imageOutputGeometryAdapter.getSelectors((state: ImageSliceState) => state.imageOutputGeometry);
export const imageContourGenerationAdapterSelectors = imageContourGenerationAdapter.getSelectors((state: ImageSliceState) => state.imageContourGeneration);
export const imagePostProcessingAdapterSelectors = imagePostProcessingAdapter.getSelectors((state: ImageSliceState) => state.imagePostProcessing);
export const imageDicomTagAdapterSelectors = imageDicomTagAdapter.getSelectors((state: ImageSliceState) => state.imageDicomTags);
export const keepLargestComponentAdapterSelectors = keepLargestComponentAdapter.getSelectors((state: ImageSliceState) => state.keepLargestComponent);
export const fillHolesAdapterSelectors = fillHolesAdapter.getSelectors((state: ImageSliceState) => state.fillHoles);
export const bodyMaskAdapterSelectors = bodyMaskAdapter.getSelectors((state: ImageSliceState) => state.bodyMasks);
export const holeMaskAdapterSelectors = holeMaskAdapter.getSelectors((state: ImageSliceState) => state.holeMasks);


export const aeTitleRuleAdapterSelectors = aeTitleRuleAdapter.getSelectors((state: ImageSliceState) => state.aeTitleRules);
export const dicomRuleAdapterSelectors = dicomRuleAdapter.getSelectors((state: ImageSliceState) => state.dicomRules);
export const dicomAttributeRuleAdapterSelectors = dicomAttributeRuleAdapter.getSelectors((state: ImageSliceState) => state.dicomAttributeRules);


/** An entity adapter for CRUD operations for backend-based customization validation errors */
export const backendValidationErrorAdapter = createEntityAdapter<BackendValidationErrorViewModel>();
// selectors for 'original' data
export const originalOutputMetadataAdapterSelectors = outputMetadataAdapter.getSelectors((state: ImageSliceState) => state.originalOutputMetadata);
export const originalImageDicomTagAdapterSelectors = imageDicomTagAdapter.getSelectors((state: ImageSliceState) => state.originalImageDicomTags);

// validation error selectors
export const customizationValidationErrorAdapterSelectors = backendValidationErrorAdapter.getSelectors((state: ImageSliceState) => state.backendValidationErrors);


/** Type guard for ImageSliceState type. */
export const isImageSliceState = (obj: unknown): obj is ImageSliceState => {
    return has(obj, 'models') &&
        has(obj, 'imageOutputs') &&
        has(obj, 'imageDicomRestrictions') &&
        has(obj, 'imageOutputGeometry') &&
        has(obj, 'imageContourGeneration') &&
        has(obj, 'imagePostProcessing') &&
        has(obj, 'imageDicomRestrictions') &&
        has(obj.imageDicomRestrictions, 'entities') &&
        isObject(obj.imageDicomRestrictions.entities);
}


export type ImageSliceState = {
    /** In-memory CRUD-based normalized data structure for customization objects modelling image models. */
    models: EntityState<Model, string>,
    /** In-memory CRUD-based normalized data structure for customization objects modelling customization base objects. */
    customizationBases: EntityState<CustomizationBase, string>,
    /** In-memory CRUD-based normalized data structure for customization objects modelling customization outputs. */
    imageOutputs: EntityState<ImageCustomizationOutput, string>,
    /** In-memory CRUD-based normalized data structure for customization objects modelling customization metadata. */
    outputMetadata: EntityState<OutputMetadataItem, string>,

    /** In-memory CRUD-based normalized data structure for customization objects modelling image+ dicom restriction objects. */
    imageDicomRestrictions: EntityState<ImageDicomRestriction, string>,
    /** In-memory CRUD-based normalized data structure for customization objects modelling image+ output geometry objects. */
    imageOutputGeometry: EntityState<ImageOutputGeometry, string>,
    /** In-memory CRUD-based normalized data structure for customization objects modelling image+ contour generation objects. */
    imageContourGeneration: EntityState<ImageContourGeneration, string>,
    /** In-memory CRUD-based normalized data structure for customization objects modelling image+ post-processing objects. */
    imagePostProcessing: EntityState<ImagePostProcessing, string>,
    /** In-memory CRUD-based normalized data structure for customization objects modelling image+ dicom restriction tag objects. */
    imageDicomTags: EntityState<ImageDicomTag, string>,
    /** In-memory CRUD-based normalized data structure for customization objects modelling image+ keep largest component post-processing objects. */
    keepLargestComponent: EntityState<KeepLargestComponent, string>,
    /** In-memory CRUD-based normalized data structure for customization objects modelling image+ fill holes post-processing objects. */
    fillHoles: EntityState<FillHoles, string>,
    /** In-memory CRUD-based normalized data structure for customization objects modelling image+ body mask post-processing objects. */
    bodyMasks: EntityState<BodyMask, string>,
    /** In-memory CRUD-based normalized data structure for customization objects modelling image+ hole mask post-processing objects. */
    holeMasks: EntityState<HoleMask, string>,

    /** In-memory CRUD-based normalized data structure for customization objects modelling AE Title-based model selection rules. */
    aeTitleRules: EntityState<AeTitleRule, string>,
    /** In-memory CRUD-based normalized data structure for customization objects modelling DICOM-based model selection rules. */
    dicomRules: EntityState<DicomRule, string>,
    /** In-memory CRUD-based normalized data structure for customization objects modelling DICOM attribute rules within DICOM-based model selection rules. */
    dicomAttributeRules: EntityState<DicomAttributeRule, string>,



    // these are for storing data directly from the backend, so any local modifications can be easily reverted back to them

    /** Original, unmodified directly-from-backend versions of image+ model customization data objects. These are stored 
     * mainly so reverting changes back to original data is easy. */
    originalModels: EntityState<Model, string>;
    /** Original, unmodified directly-from-backend versions of image+ customization base data objects. These are stored 
     * mainly so reverting changes back to original data is easy. */
    originalCustomizationBases: EntityState<CustomizationBase, string>;
    /** Original, unmodified directly-from-backend versions of image+ customization output data objects. These are stored 
     * mainly so reverting changes back to original data is easy. */
    originalImageOutputs: EntityState<ImageCustomizationOutput, string>;
    /** Original, unmodified directly-from-backend versions of image+ customization metadata objects. These are stored 
     * mainly so reverting changes back to original data is easy. */
    originalOutputMetadata: EntityState<OutputMetadataItem, string>;

    /** Original, unmodified directly-from-backend versions of image+ dicom restriction objects. These are stored 
     * mainly so reverting changes back to original data is easy. */
    originalImageDicomRestrictions: EntityState<ImageDicomRestriction, string>,
    /** Original, unmodified directly-from-backend versions of image+ output geometry objects. These are stored 
     * mainly so reverting changes back to original data is easy. */
    originalImageOutputGeometry: EntityState<ImageOutputGeometry, string>,
    /** Original, unmodified directly-from-backend versions of image+ contour generation objects. These are stored 
     * mainly so reverting changes back to original data is easy. */
    originalImageContourGeneration: EntityState<ImageContourGeneration, string>,
    /** Original, unmodified directly-from-backend versions of image+ post-processing objects. These are stored 
     * mainly so reverting changes back to original data is easy. */
    originalImagePostProcessing: EntityState<ImagePostProcessing, string>,
    /** Original, unmodified directly-from-backend versions of image+ dicom restriction tag objects. These are stored 
     * mainly so reverting changes back to original data is easy. */
    originalImageDicomTags: EntityState<ImageDicomTag, string>,
    /** Original, unmodified directly-from-backend versions of image+ keep largest component post-processing objects. These are stored 
     * mainly so reverting changes back to original data is easy. */
    originalKeepLargestComponent: EntityState<KeepLargestComponent, string>,
    /** Original, unmodified directly-from-backend versions of image+ fill holes post-processing objects. These are stored 
     * mainly so reverting changes back to original data is easy. */
    originalFillHoles: EntityState<FillHoles, string>,
    /** Original, unmodified directly-from-backend versions of image+ body mask post-processing objects. These are stored 
     * mainly so reverting changes back to original data is easy. */
    originalBodyMasks: EntityState<BodyMask, string>,
    /** Original, unmodified directly-from-backend versions of image+ hole mask post-processing objects. These are stored 
     * mainly so reverting changes back to original data is easy. */
    originalHoleMasks: EntityState<HoleMask, string>,

    /** Original, unmodified directly-from-backend versions of image+ AE Title rule data objects. These are stored 
     * mainly so reverting changes back to original data is easy. */
    originalAeTitleRules: EntityState<AeTitleRule, string>;
    /** Original, unmodified directly-from-backend versions of image+ DICOM rule data objects. These are stored 
     * mainly so reverting changes back to original data is easy. */
    originalDicomRules: EntityState<DicomRule, string>;
    /** Original, unmodified directly-from-backend versions of image+ DICOM attribute rule data objects. These are stored 
     * mainly so reverting changes back to original data is easy. */
    originalDicomAttributeRules: EntityState<DicomAttributeRule, string>;


    /** Image+ customization validation errors coming from backend. */
    backendValidationErrors: EntityState<BackendValidationErrorViewModel, string>,

    /** An error that occurred when fetching image+ customizations, or null. */
    customizationFetchError: string | null,

    /** Saving of model customizations to backend is in progress if true, false otherwise. */
    isModelCustomizationSavingInProgress: boolean,

    /** True if resetting all image+ customizations to factory defaults is currently in progress, false otherwise. */
    isAllModelCustomizationsResetInProgress: boolean,

    /** An error that occurred when resetting all model customizations to factory defaults, or null if no error occurred. */
    allModelCustomizationsResetError: string | null,

    /** True if resetting a single image+ customization output to factory defaults is currently in progress, false otherwise. */
    isOutputResetInProgress: boolean,

    /** Model customization save error, if any. */
    modelCustomizationSaveError: string | null,

    /** A concurrent save error, if any. Set when the backend detects another user has saved changes. */
    concurrentSaveError: ConcurrentSaveError | null,

    /** An error regarding model customization data that's not directly related to saving */
    modelCustomizationDataError: string | null,

    /** An error message regarding exporting or importing entire customization config to/from JSON */
    customizationImportExportError: string | null,

    /** True if a PDF export is currently in progress. */
    pdfExportInProgress: boolean,

    /** Current user selection of tags to filter models by. No filtering is applied if empty. */
    activeTagFilters: string[],

    /** Current user selection of input modalities to filter models by. No filtering is applied if empty. */
    activeInputModalityFilters: Modality[],

    /** Current user selection of output modalities to filter models by. No filtering is applied if empty. */
    activeOutputModalityFilters: Modality[],

    /** Current user selection of active version tags to filter models by. No filtering is applied if empty. */
    activeVersionTags: string[],

    /** Current user selection of free-text filter value. No filtering is applied if empty. */
    activeFreeTextFilter: string,

    /** True if deprecated models are shown, false otherwise. */
    areDeprecatedModelsShown: boolean,

    /** True if models not within current license are shown, false otherwise. */
    areNonLicensedModelsShown: boolean,

    /** True if models hidden by current user are shown, false otherwise. */
    areUserHiddenModelsShown: boolean,
};



export const initialState: ImageSliceState = {
    models: modelAdapter.getInitialState(),
    customizationBases: customizationBaseAdapter.getInitialState(),
    imageOutputs: imageOutputAdapter.getInitialState(),
    outputMetadata: outputMetadataAdapter.getInitialState(),

    imageDicomRestrictions: imageDicomRestrictionAdapter.getInitialState(),
    imageOutputGeometry: imageOutputGeometryAdapter.getInitialState(),
    imageContourGeneration: imageContourGenerationAdapter.getInitialState(),
    imagePostProcessing: imagePostProcessingAdapter.getInitialState(),
    imageDicomTags: imageDicomTagAdapter.getInitialState(),
    keepLargestComponent: keepLargestComponentAdapter.getInitialState(),
    fillHoles: fillHolesAdapter.getInitialState(),
    bodyMasks: bodyMaskAdapter.getInitialState(),
    holeMasks: holeMaskAdapter.getInitialState(),

    aeTitleRules: aeTitleRuleAdapter.getInitialState(),
    dicomRules: dicomRuleAdapter.getInitialState(),
    dicomAttributeRules: dicomAttributeRuleAdapter.getInitialState(),

    originalModels: modelAdapter.getInitialState(),
    originalCustomizationBases: customizationBaseAdapter.getInitialState(),
    originalImageOutputs: imageOutputAdapter.getInitialState(),
    originalOutputMetadata: outputMetadataAdapter.getInitialState(),
    originalImageDicomRestrictions: imageDicomRestrictionAdapter.getInitialState(),
    originalImageOutputGeometry: imageOutputGeometryAdapter.getInitialState(),
    originalImageContourGeneration: imageContourGenerationAdapter.getInitialState(),
    originalImagePostProcessing: imagePostProcessingAdapter.getInitialState(),
    originalImageDicomTags: imageDicomTagAdapter.getInitialState(),
    originalKeepLargestComponent: keepLargestComponentAdapter.getInitialState(),
    originalFillHoles: fillHolesAdapter.getInitialState(),
    originalBodyMasks: bodyMaskAdapter.getInitialState(),
    originalHoleMasks: holeMaskAdapter.getInitialState(),
    originalAeTitleRules: aeTitleRuleAdapter.getInitialState(),
    originalDicomRules: dicomRuleAdapter.getInitialState(),
    originalDicomAttributeRules: dicomAttributeRuleAdapter.getInitialState(),

    backendValidationErrors: backendValidationErrorAdapter.getInitialState(),

    customizationFetchError: null,
    isModelCustomizationSavingInProgress: false,
    isAllModelCustomizationsResetInProgress: false,
    allModelCustomizationsResetError: null,
    isOutputResetInProgress: false,
    modelCustomizationSaveError: null,
    concurrentSaveError: null,
    modelCustomizationDataError: null,
    customizationImportExportError: null,
    pdfExportInProgress: false,
    activeTagFilters: [],
    activeInputModalityFilters: [],
    activeOutputModalityFilters: [],
    activeVersionTags: [],
    activeFreeTextFilter: '',
    areDeprecatedModelsShown: false,
    areNonLicensedModelsShown: false,
    areUserHiddenModelsShown: false,
};



const sliceWrapper = new ImageSliceWrapper();



/** Redux store slice for interacting with image+ configuration and customization. */
const imageSlice = createSlice({
    name: 'image',
    initialState,
    reducers: {
        /**
         * Sets current image model customization entities to provided values. This is used when initializing, swapping, or
         * resetting entire model customizations loaded into memory.
         * @param action.customizations All image-related model customization entities to load into memory. If set to undefined then
         * in-model entities are reset to default (not original!) empty values.
         * @param action.errorMessage Optional error message.
         * @param action.retainOriginalData If true, the original entities are not reset to the new values, but rather kept as they are.  This is
         * relevant when e.g. setting customizations during JSON import where we want to keep the original values intact so that we can
         * still perform a full undo to pre-import values. Note: ignored if customizations is set to null, in which case the original data is 
         * always reset to default (not original!) values.
         */
        modelCustomizationsSet(state, action: PayloadAction<{ customizations: ImageCustomizationEntities | null, errorMessage?: string, retainOriginalData?: boolean }>) {
            const { customizations, errorMessage, retainOriginalData } = action.payload;

            if (customizations !== null) {
                // set everything according to input
                modelAdapter.setAll(state.models, customizations.models);
                customizationBaseAdapter.setAll(state.customizationBases, customizations.customizationBases);
                imageOutputAdapter.setAll(state.imageOutputs, customizations.imageOutputs);
                outputMetadataAdapter.setAll(state.outputMetadata, customizations.outputMetadata);

                imageDicomRestrictionAdapter.setAll(state.imageDicomRestrictions, customizations.imageDicomRestrictions);
                imageOutputGeometryAdapter.setAll(state.imageOutputGeometry, customizations.imageOutputGeometry);
                imageContourGenerationAdapter.setAll(state.imageContourGeneration, customizations.imageContourGeneration);
                imagePostProcessingAdapter.setAll(state.imagePostProcessing, customizations.imagePostProcessing);
                imageDicomTagAdapter.setAll(state.imageDicomTags, customizations.imageDicomTags);
                keepLargestComponentAdapter.setAll(state.keepLargestComponent, customizations.keepLargestComponent);
                fillHolesAdapter.setAll(state.fillHoles, customizations.fillHoles);
                bodyMaskAdapter.setAll(state.bodyMasks, customizations.bodyMasks);
                holeMaskAdapter.setAll(state.holeMasks, customizations.holeMasks);

                aeTitleRuleAdapter.setAll(state.aeTitleRules, customizations.aeTitleRules);
                dicomRuleAdapter.setAll(state.dicomRules, customizations.dicomRules);
                dicomAttributeRuleAdapter.setAll(state.dicomAttributeRules, customizations.dicomAttributeRules);

                // set original collections, also, so we can revert changes to the actual collection with ease
                if (!retainOriginalData) {
                    modelAdapter.setAll(state.originalModels, customizations.models);
                    customizationBaseAdapter.setAll(state.originalCustomizationBases, customizations.customizationBases);
                    imageOutputAdapter.setAll(state.originalImageOutputs, customizations.imageOutputs);
                    outputMetadataAdapter.setAll(state.originalOutputMetadata, customizations.outputMetadata);

                    imageDicomRestrictionAdapter.setAll(state.originalImageDicomRestrictions, customizations.imageDicomRestrictions);
                    imageOutputGeometryAdapter.setAll(state.originalImageOutputGeometry, customizations.imageOutputGeometry);
                    imageContourGenerationAdapter.setAll(state.originalImageContourGeneration, customizations.imageContourGeneration);
                    imagePostProcessingAdapter.setAll(state.originalImagePostProcessing, customizations.imagePostProcessing);
                    imageDicomTagAdapter.setAll(state.originalImageDicomTags, customizations.imageDicomTags);
                    keepLargestComponentAdapter.setAll(state.originalKeepLargestComponent, customizations.keepLargestComponent);
                    fillHolesAdapter.setAll(state.originalFillHoles, customizations.fillHoles);
                    bodyMaskAdapter.setAll(state.originalBodyMasks, customizations.bodyMasks);
                    holeMaskAdapter.setAll(state.originalHoleMasks, customizations.holeMasks);

                    aeTitleRuleAdapter.setAll(state.originalAeTitleRules, customizations.aeTitleRules);
                    dicomRuleAdapter.setAll(state.originalDicomRules, customizations.dicomRules);
                    dicomAttributeRuleAdapter.setAll(state.originalDicomAttributeRules, customizations.dicomAttributeRules);
                }
            } else {
                // set everything to default values
                modelAdapter.setAll(state.models, modelAdapter.getInitialState().entities as Record<string, Model>);
                customizationBaseAdapter.setAll(state.customizationBases, customizationBaseAdapter.getInitialState().entities as Record<string, CustomizationBase>);
                imageOutputAdapter.setAll(state.imageOutputs, imageOutputAdapter.getInitialState().entities as Record<string, ImageCustomizationOutput>);
                outputMetadataAdapter.setAll(state.outputMetadata, outputMetadataAdapter.getInitialState().entities as Record<string, OutputMetadataItem>);

                imageDicomRestrictionAdapter.setAll(state.imageDicomRestrictions, imageDicomRestrictionAdapter.getInitialState().entities as Record<string, ImageDicomRestriction>);
                imageOutputGeometryAdapter.setAll(state.imageOutputGeometry, imageOutputGeometryAdapter.getInitialState().entities as Record<string, ImageOutputGeometry>);
                imageContourGenerationAdapter.setAll(state.imageContourGeneration, imageContourGenerationAdapter.getInitialState().entities as Record<string, ImageContourGeneration>);
                imagePostProcessingAdapter.setAll(state.imagePostProcessing, imagePostProcessingAdapter.getInitialState().entities as Record<string, ImagePostProcessing>);
                imageDicomTagAdapter.setAll(state.imageDicomTags, imageDicomTagAdapter.getInitialState().entities as Record<string, ImageDicomTag>);
                keepLargestComponentAdapter.setAll(state.keepLargestComponent, keepLargestComponentAdapter.getInitialState().entities as Record<string, KeepLargestComponent>);
                fillHolesAdapter.setAll(state.fillHoles, fillHolesAdapter.getInitialState().entities as Record<string, FillHoles>);
                bodyMaskAdapter.setAll(state.bodyMasks, bodyMaskAdapter.getInitialState().entities as Record<string, BodyMask>);
                holeMaskAdapter.setAll(state.holeMasks, holeMaskAdapter.getInitialState().entities as Record<string, HoleMask>);

                aeTitleRuleAdapter.setAll(state.aeTitleRules, aeTitleRuleAdapter.getInitialState().entities as Record<string, AeTitleRule>);
                dicomRuleAdapter.setAll(state.dicomRules, dicomRuleAdapter.getInitialState().entities as Record<string, DicomRule>);
                dicomAttributeRuleAdapter.setAll(state.dicomAttributeRules, dicomAttributeRuleAdapter.getInitialState().entities as Record<string, DicomAttributeRule>);

                // set original collections, also, so we can revert changes to the actual collection with ease
                modelAdapter.setAll(state.originalModels, modelAdapter.getInitialState().entities as Record<string, Model>);
                customizationBaseAdapter.setAll(state.originalCustomizationBases, customizationBaseAdapter.getInitialState().entities as Record<string, CustomizationBase>);
                imageOutputAdapter.setAll(state.originalImageOutputs, imageOutputAdapter.getInitialState().entities as Record<string, ImageCustomizationOutput>);
                outputMetadataAdapter.setAll(state.originalOutputMetadata, outputMetadataAdapter.getInitialState().entities as Record<string, OutputMetadataItem>);

                imageDicomRestrictionAdapter.setAll(state.originalImageDicomRestrictions, imageDicomRestrictionAdapter.getInitialState().entities as Record<string, ImageDicomRestriction>);
                imageOutputGeometryAdapter.setAll(state.originalImageOutputGeometry, imageOutputGeometryAdapter.getInitialState().entities as Record<string, ImageOutputGeometry>);
                imageContourGenerationAdapter.setAll(state.originalImageContourGeneration, imageContourGenerationAdapter.getInitialState().entities as Record<string, ImageContourGeneration>);
                imagePostProcessingAdapter.setAll(state.originalImagePostProcessing, imagePostProcessingAdapter.getInitialState().entities as Record<string, ImagePostProcessing>);
                imageDicomTagAdapter.setAll(state.originalImageDicomTags, imageDicomTagAdapter.getInitialState().entities as Record<string, ImageDicomTag>);
                keepLargestComponentAdapter.setAll(state.originalKeepLargestComponent, keepLargestComponentAdapter.getInitialState().entities as Record<string, KeepLargestComponent>);
                fillHolesAdapter.setAll(state.originalFillHoles, fillHolesAdapter.getInitialState().entities as Record<string, FillHoles>);
                bodyMaskAdapter.setAll(state.originalBodyMasks, bodyMaskAdapter.getInitialState().entities as Record<string, BodyMask>);
                holeMaskAdapter.setAll(state.originalHoleMasks, holeMaskAdapter.getInitialState().entities as Record<string, HoleMask>);

                aeTitleRuleAdapter.setAll(state.originalAeTitleRules, aeTitleRuleAdapter.getInitialState().entities as Record<string, AeTitleRule>);
                dicomRuleAdapter.setAll(state.originalDicomRules, dicomRuleAdapter.getInitialState().entities as Record<string, DicomRule>);
                dicomAttributeRuleAdapter.setAll(state.originalDicomAttributeRules, dicomAttributeRuleAdapter.getInitialState().entities as Record<string, DicomAttributeRule>);
            }

            state.customizationFetchError = errorMessage !== undefined ? errorMessage : null;
            if (errorMessage === undefined) {
                // clear any errors
                backendValidationErrorAdapter.removeAll(state.backendValidationErrors);
                sliceWrapper.clearSaveErrors(state);
            }
        },

        /**
         * Updates a image model's visibility.
         * @param action.modelId Internal (entity adapter) ID of the image model to update.
         * @param action.visibility The new visibility setting for the image model.
         */
        modelVisibilityUpdated(state, action: PayloadAction<{ modelId: string, visibility: ModelVisibility }>) {
            const { modelId, visibility } = action.payload;
            sliceWrapper.updateModelVisibility(state, modelId, visibility);
        },

        /**
        * Updates the description field on a customization base object.
        * @param action.customizationBaseId Internal (entity adapter) ID of the customization base to update.
        * @param action.description The new description to set.
        */
        modelCustomizationDescriptionUpdated(state, action: PayloadAction<{ customizationBaseId: string, description: string }>) {
            const { customizationBaseId, description } = action.payload;
            sliceWrapper.updateModelCustomizationDescription(state, customizationBaseId, description);
        },

        /**
         * Updates an output metadata item tuple. If either attribute or value is left as undefined then that particular prop is not updated
         * and the existing prop is left unchanged.
         * @param action.metadataId Internal (entity adapter) ID of the output metadata item that will be updated.
         * @param action.attribute The new attribute name of the metadata item that will be used, or undefined if attribute should not be changed.
         * @param action.value The new value of the metadata item, or undefined if value should not be changed.
         * @param action.isUndoOperation Marks this update as part of an undo operation. Note that the correct reverted attribute and value props
         * must be supplied to this function manually, this function does not perform an undo by itself. Optional, defaults to false.
         */
        metadataItemUpdated(state, action: PayloadAction<{ metadataId: string, attribute: string | undefined, value: string | undefined, isUndoOperation?: boolean }>) {
            const { metadataId, attribute, value, isUndoOperation } = action.payload;
            sliceWrapper.updateMetadataItem(state, metadataId, attribute, value, isUndoOperation);
        },

        /**
         * Removes an output metadata item.
         * @param action.metadataId Internal (entity adapter) ID of the output metadata item that will be removed.
         */
        metadataItemRemoved(state, action: PayloadAction<string>) {
            const metadataId = action.payload;
            sliceWrapper.removeMetadataItem(state, metadataId);
        },

        /**
         * Removes a tag from a (newly created) customization metadata item that would mark it as being the target of being
         * automatically scrolled to in UI.
         * 
         * This tag is generally given to metadata items after their creation in the UI and should be removed once
         * the UI has scrolled to the correct position in the UI once.
         * 
         * @param action Internal (entity adapter) ID of the customization metadata item where this tag is to be removed.
         */
        scrollToViewFromModelCustomizationMetadataRemoved(state, action: PayloadAction<string>) {
            const metadataId = action.payload;
            sliceWrapper.removeScrollToViewFromMetadata(state, metadataId);
        },

        /**
        * Adds supplied AE Title rule into image customization configuration.
        * @param action The AE Title rule to add.
        */
        aeTitleRuleAdded(state, action: PayloadAction<AeTitleRule>) {
            const aeTitleRule = action.payload;
            sliceWrapper.addAeTitleRule(state, aeTitleRule);
        },

        /**
         * Updates the 'action' field in an AE Title rule.
         * @param action.id Internal (entity adapter) ID of the AE Title rule to update.
         * @param action.action The new action value for the rule.
         */
        aeTitleRuleActionUpdated(state, action: PayloadAction<{ id: string, action: string }>) {
            const { id, action: newAction } = action.payload;
            sliceWrapper.updateAeTitleRuleAction(state, id, newAction);
        },

        /**
         * Removes an AE Title rule from image customization configuration.
         * Throws if the AE Title rule to be removed is not found from configuration.
         * @param action Internal (entity adapter) ID of the AE Title rule to remove.
         */
        aeTitleRuleRemoved(state, action: PayloadAction<string>) {
            const id = action.payload;
            sliceWrapper.removeAeTitleRule(state, id);
        },

        /**
         * Adds supplied DICOM rule into image customization configuration.
         * Note that DICOM attribute rules under a DICOM rule must be added separately.
         * @param action The DICOM rule to add.
         */
        dicomRuleAdded(state, action: PayloadAction<DicomRule>) {
            const dicomRule = action.payload;
            sliceWrapper.addDicomRule(state, dicomRule);
        },

        /**
         * Removes a DICOM rule from image customization configuration. Also removes any DICOM attribute
         * rules under the DICOM rule.
         * Throws if the DICOM rule to be removed is not found from configuration.
         * @param action Internal (entity adapter) ID of the DICOM rule to remove.
         */
        dicomRuleRemoved(state, action: PayloadAction<string>) {
            const id = action.payload;
            sliceWrapper.removeDicomRule(state, id);
        },

        /**
        * Adds supplied DICOM attribute rule into image customization configuration.
        * Note that the parent DICOM rule must already exist in configuration or this function will throw.
        * @param action The DICOM attribute rule to add.
        */
        dicomAttributeRuleAdded(state, action: PayloadAction<DicomAttributeRule>) {
            const dicomAttributeRule = action.payload;
            sliceWrapper.addDicomAttributeRule(state, dicomAttributeRule);
        },

        /**
         * Removes a DICOM attribute rule from image customization configuration.
         * Throws if the DICOM attribute rule to be removed or its parent DICOM rule are not found from configuration.
         * @param action Internal (entity adapter) ID of the DICOM attribute rule to remove.
         */
        dicomAttributeRuleRemoved(state, action: PayloadAction<string>) {
            const id = action.payload;
            sliceWrapper.removeDicomAttributeRule(state, id);
        },

        /**
         * Updates a DICOM attribute rule.
         * @param action.id Internal (entity adapter) ID of the DICOM attribute rule to update.
         * @param action.dicomAttribute The attribute field of the rule is updated to this value.
         * @param action.dicomValue The DICOM value field of the rule is updated to this value.
         */
        dicomAttributeRuleUpdated(state, action: PayloadAction<{ id: string, dicomAttribute: string, dicomValue: string }>) {
            const { id, dicomAttribute, dicomValue } = action.payload;
            sliceWrapper.updateDicomAttributeRule(state, id, dicomAttribute, dicomValue);
        },

        /**
         * Updates an image dicom restriction's "enabled" status.
         * @param action.id ID of the dicom restriction.
         * @param action.isEnabled Whether dicom restriction will be enabled (true) or disabled (false).
         */
        imageDicomRestrictionEnabledSet(state, action: PayloadAction<{ id: string, isEnabled: boolean }>) {
            const { id, isEnabled } = action.payload;

            const dicomRestriction = state.imageDicomRestrictions.entities[id];
            if (dicomRestriction === undefined) { throw new Error(`Could not find dicom restriction with id ${id}`); }
            if (dicomRestriction.outputId === undefined) { throw new Error(`Could not find parent output for dicom restriction with id ${id}`); }

            // if being enabled, add a couple of empty tags if there's currently none
            let tags = dicomRestriction.tags;
            if (isEnabled && tags.length === 0) {
                const tag1 = createNewImageDicomTag('', literalTextToRegex(''), id, undefined, true);
                const tag2 = createNewImageDicomTag('', literalTextToRegex(''), id, undefined, true);
                imageDicomTagAdapter.addMany(state.imageDicomTags, [tag1, tag2]);
                tags = [tag1.id, tag2.id];
            }

            imageDicomRestrictionAdapter.updateOne(state.imageDicomRestrictions, { id: id, changes: { isEnabled, tags } });

            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.DicomRestriction);
        },

        /**
        * Adds a new DICOM tag with default settings to the given image dicom restriction.
        * @param action ID of the dicom restriction.
        */
        newDicomTagAdded(state, action: PayloadAction<string>) {
            const dicomRestrictionId = action.payload;

            const dicomRestriction = state.imageDicomRestrictions.entities[dicomRestrictionId];
            if (dicomRestriction === undefined) { throw new Error(`Could not find dicom restriction with id ${dicomRestrictionId}`); }
            if (dicomRestriction.outputId === undefined) { throw new Error(`Could not find parent output for dicom restriction with id ${dicomRestrictionId}`); }

            const dicomTag = createNewImageDicomTag('', literalTextToRegex(''), dicomRestrictionId, undefined, true);
            imageDicomTagAdapter.addOne(state.imageDicomTags, dicomTag);
            imageDicomRestrictionAdapter.updateOne(state.imageDicomRestrictions, {
                id: dicomRestrictionId,
                changes: { tags: dicomRestriction.tags ? dicomRestriction.tags.concat(dicomTag.id) : [dicomTag.id] }
            });
            setObjectAndAncestorsAsModified(state, [dicomTag.id], CustomizationObjectType.DicomTag);
        },

        /**
         * Sets a DICOM tag's attribute.
         * @param action.id ID of the dicom tag.
         * @param action.attribute The value to set the attribute field to.
         */
        dicomTagAttributeSet(state, action: PayloadAction<{ id: string, attribute: string }>) {
            const { id, attribute } = action.payload;

            const dicomTag = state.imageDicomTags.entities[id];
            if (dicomTag === undefined) { throw new Error(`Could not find DICOM tag with id ${id}`); }

            imageDicomTagAdapter.updateOne(state.imageDicomTags, { id: id, changes: { attribute } });
            setObjectAndAncestorsAsModified(state, [dicomTag.id], CustomizationObjectType.DicomTag);
        },

        /**
         * Sets a DICOM tag's value.
         * @param action.id ID of the dicom tag.
         * @param action.value The value to set the value field to.
         */
        dicomTagValueSet(state, action: PayloadAction<{ id: string, value: string }>) {
            const { id, value } = action.payload;

            const dicomTag = state.imageDicomTags.entities[id];
            if (dicomTag === undefined) { throw new Error(`Could not find DICOM tag with id ${id}`); }

            imageDicomTagAdapter.updateOne(state.imageDicomTags, { id: id, changes: { value } });
            setObjectAndAncestorsAsModified(state, [dicomTag.id], CustomizationObjectType.DicomTag);
        },

        /**
         * Removes dicom tag with given ID
         * @param action ID of the dicom tag.
         */
        dicomTagRemoved(state, action: PayloadAction<string>) {
            const dicomTagId = action.payload;

            const dicomTag = state.imageDicomTags.entities[dicomTagId];
            if (dicomTag === undefined) { throw new Error(`Could not find dicom tag with id ${dicomTagId}`); }

            const dicomRestrictionId = dicomTag.dicomRestrictionId;
            if (!dicomRestrictionId) { throw new Error(`Dicom tag ${dicomTagId} is not linked to a valid dicom restriction object`); }
            const dicomRestriction = state.imageDicomRestrictions.entities[dicomRestrictionId];
            if (dicomRestriction === undefined) { throw new Error(`Could not find dicom restriction with id ${dicomRestrictionId}`); }
            if (dicomRestriction.outputId === undefined) { throw new Error(`Could not find parent output for dicom restriction with id ${dicomRestrictionId}`); }

            imageDicomRestrictionAdapter.updateOne(state.imageDicomRestrictions, { id: dicomRestrictionId, changes: { tags: dicomRestriction.tags ? dicomRestriction.tags.filter(tId => tId !== dicomTagId) : [] } });
            imageDicomTagAdapter.removeOne(state.imageDicomTags, dicomTagId);

            setObjectAndAncestorsAsModified(state, [dicomRestrictionId], CustomizationObjectType.DicomRestriction);
        },

        /**
         * Updates an image output geometry's "match input geometry" status.
         * @param action.id ID of the output geometry.
         * @param action.matchInput Whether output geometry will match input (true) or not (false).
         */
        imageOutputGeometryMatchInputSet(state, action: PayloadAction<{ id: string, matchInput: boolean }>) {
            const { id, matchInput } = action.payload;

            const outputGeometry = state.imageOutputGeometry.entities[id];
            if (outputGeometry === undefined) { throw new Error(`Could not find output geometry with id ${id}`); }

            imageOutputGeometryAdapter.updateOne(state.imageOutputGeometry, { id: id, changes: { matchInputGeometry: matchInput } });

            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.OutputGeometry);
        },

        /**
         * Sets an image output geometry's FOV size field.
         * @param action.id ID of the output geometry.
         * @param action.fovSize The size to set FOV to.
         */
        imageOutputGeometryFovSizeSet(state, action: PayloadAction<{ id: string, fovSize: LengthValue }>) {
            const { id, fovSize } = action.payload;

            const outputGeometry = state.imageOutputGeometry.entities[id];
            if (outputGeometry === undefined) { throw new Error(`Could not find output geometry with id ${id}`); }

            imageOutputGeometryAdapter.updateOne(state.imageOutputGeometry, { id: id, changes: { fovSize } });

            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.OutputGeometry);
        },

        /**
         * Sets an image output geometry's matrix size field.
         * @param action.id ID of the output geometry.
         * @param action.matrixSize The value to set matrix size to.
         */
        imageOutputGeometryMatrixSizeSet(state, action: PayloadAction<{ id: string, matrixSize: number }>) {
            const { id, matrixSize } = action.payload;

            const outputGeometry = state.imageOutputGeometry.entities[id];
            if (outputGeometry === undefined) { throw new Error(`Could not find output geometry with id ${id}`); }

            imageOutputGeometryAdapter.updateOne(state.imageOutputGeometry, { id: id, changes: { matrixSize } });

            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.OutputGeometry);
        },

        /**
         * Updates an image output geometry's "match input slice thickness" status.
         * @param action.id ID of the output geometry.
         * @param action.matchInputSliceThickness Whether output geometry will match input slice thickness (true) or not (false).
         */
        imageOutputGeometryMatchInputSliceThicknessSet(state, action: PayloadAction<{ id: string, matchInputSliceThickness: boolean }>) {
            const { id, matchInputSliceThickness } = action.payload;

            const outputGeometry = state.imageOutputGeometry.entities[id];
            if (outputGeometry === undefined) { throw new Error(`Could not find output geometry with id ${id}`); }

            imageOutputGeometryAdapter.updateOne(state.imageOutputGeometry, {
                id: id, changes: {
                    sliceThickness: {
                        ...outputGeometry.sliceThickness,
                        matchInput: matchInputSliceThickness
                    }
                }
            });

            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.OutputGeometry);
        },

        /**
         * Sets an image output geometry's output slice thickness.
         * @param action.id ID of the output geometry.
         * @param action.sliceThickness The size to set output slice thickness to.
         */
        imageOutputGeometryOutputSliceThicknessSet(state, action: PayloadAction<{ id: string, sliceThickness: LengthValue }>) {
            const { id, sliceThickness } = action.payload;

            const outputGeometry = state.imageOutputGeometry.entities[id];
            if (outputGeometry === undefined) { throw new Error(`Could not find output geometry with id ${id}`); }

            imageOutputGeometryAdapter.updateOne(state.imageOutputGeometry,
                {
                    id: id, changes: {
                        sliceThickness: {
                            ...outputGeometry.sliceThickness,
                            sliceThickness: sliceThickness
                        }
                    }
                });

            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.OutputGeometry);
        },

        /**
         * Updates an image contour generation's "enabled" status.
         * @param action.id ID of the contour generation.
         * @param action.isEnabled Whether contour generation will be enabled (true) or disabled (false).
         */
        imageContourGenerationEnabledSet(state, action: PayloadAction<{ id: string, isEnabled: boolean }>) {
            const { id, isEnabled } = action.payload;

            const contourGeneration = state.imageContourGeneration.entities[id];
            if (contourGeneration === undefined) { throw new Error(`Could not find contour generation with id ${id}`); }
            if (contourGeneration.outputId === undefined) { throw new Error(`Could not find parent output for contour generation with id ${id}`); }

            imageContourGenerationAdapter.updateOne(state.imageContourGeneration, { id: id, changes: { isEnabled: isEnabled } });

            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.ContourGeneration);
        },

        /**
         * Updates an image contour generation's "action" status.
         * @param action.id ID of the contour generation.
         * @param action.actionName The action name to be set.
         */
        imageContourGenerationActionSet(state, action: PayloadAction<{ id: string, actionName: string }>) {
            const { id, actionName } = action.payload;

            const contourGeneration = state.imageContourGeneration.entities[id];
            if (contourGeneration === undefined) { throw new Error(`Could not find contour generation with id ${id}`); }
            if (contourGeneration.outputId === undefined) { throw new Error(`Could not find parent output for contour generation with id ${id}`); }

            imageContourGenerationAdapter.updateOne(state.imageContourGeneration, { id: id, changes: { action: actionName } });

            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.ContourGeneration);
        },

        /**
         * Updates an image contour generation's "attach to" status.
         * @param action.id ID of the contour generation.
         * @param action.attachTo Whether contour generation will be attached to output or input.
         */
        imageContourGenerationAttachToSet(state, action: PayloadAction<{ id: string, attachTo: ContourAttachSeries }>) {
            const { id, attachTo } = action.payload;

            const contourGeneration = state.imageContourGeneration.entities[id];
            if (contourGeneration === undefined) { throw new Error(`Could not find contour generation with id ${id}`); }
            if (contourGeneration.outputId === undefined) { throw new Error(`Could not find parent output for contour generation with id ${id}`); }

            imageContourGenerationAdapter.updateOne(state.imageContourGeneration, { id: id, changes: { attachTo } });

            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.ContourGeneration);
        },

        /**
         * Updates an image contour generation's "ROI physical properties enabled" status.
         * @param action.id ID of the contour generation.
         * @param action.isEnabled Whether ROI physical properties will be enabled (true) or disabled (false).
         */
        imageContourGenerationRoiPhysicalPropertiesEnabledSet(state, action: PayloadAction<{ id: string, isEnabled: boolean }>) {
            const { id, isEnabled } = action.payload;

            const contourGeneration = state.imageContourGeneration.entities[id];
            if (contourGeneration === undefined) { throw new Error(`Could not find contour generation with id ${id}`); }
            if (contourGeneration.outputId === undefined) { throw new Error(`Could not find parent output for contour generation with id ${id}`); }

            imageContourGenerationAdapter.updateOne(state.imageContourGeneration, { id: id, changes: { roiPhysicalPropertiesEnabled: isEnabled } });

            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.ContourGeneration);
        },

        /**
         * Updates an image contour generation's "ROI physical properties density type".
         * @param action.id ID of the contour generation.
         * @param action.densityType The density type to be set.
         */
        imageContourGenerationRoiPhysicalPropertiesDensityTypeSet(state, action: PayloadAction<{ id: string, densityType: PhysicalProperty }>) {
            const { id, densityType } = action.payload;

            const contourGeneration = state.imageContourGeneration.entities[id];
            if (contourGeneration === undefined) { throw new Error(`Could not find contour generation with id ${id}`); }
            if (contourGeneration.outputId === undefined) { throw new Error(`Could not find parent output for contour generation with id ${id}`); }

            imageContourGenerationAdapter.updateOne(state.imageContourGeneration, { id: id, changes: { roiPhysicalPropertiesDensityType: densityType } });

            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.ContourGeneration);
        },

        /**
         * Updates an image keep largest component post-processing entry's "enabled" status.
         * @param action.id ID of the keep largest component object.
         * @param action.isEnabled Whether keep largest component post-processing will be enabled (true) or disabled (false).
         */
        imageKeepLargestComponentEnabledSet(state, action: PayloadAction<{ id: string, isEnabled: boolean }>) {
            const { id, isEnabled } = action.payload;

            const keepLargestComponent = state.keepLargestComponent.entities[id];
            if (keepLargestComponent === undefined) { throw new Error(`Could not find keep largest component with id ${id}`); }
            if (keepLargestComponent.postProcessingId === undefined) { throw new Error(`Could not find parent post-processing for keep largest component with id ${id}`); }

            keepLargestComponentAdapter.updateOne(state.keepLargestComponent, { id: id, changes: { isEnabled: isEnabled } });

            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.KeepLargestComponent);

        },

        /**
         * Updates an image fill holes post-processing entry's "enabled" status.
         * @param action.id ID of the fill holes object.
         * @param action.isEnabled Whether fill holes post-processing will be enabled (true) or disabled (false).
         */
        imageFillHolesEnabledSet(state, action: PayloadAction<{ id: string, isEnabled: boolean }>) {
            const { id, isEnabled } = action.payload;

            const fillHoles = state.fillHoles.entities[id];
            if (fillHoles === undefined) { throw new Error(`Could not find fill holes object with id ${id}`); }
            if (fillHoles.postProcessingId === undefined) { throw new Error(`Could not find parent post-processing for fill holes object with id ${id}`); }

            fillHolesAdapter.updateOne(state.fillHoles, { id: id, changes: { isEnabled: isEnabled } });

            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.FillHoles);

        },

        /**
         * Updates an image restore non-human anatomy post-processing entry's "enabled" status.
         * @param action.id ID of the post processing object.
         * @param action.isEnabled Whether restore non-human anatomy post-processing will be enabled (true) or disabled (false).
         */
        imageRestoreNonHumanAnatomyEnabledSet(state, action: PayloadAction<{ id: string, isEnabled: boolean }>) {
            const { id, isEnabled } = action.payload;

            const postProcessing = state.imagePostProcessing.entities[id];
            if (postProcessing === undefined) { throw new Error(`Could not find post-processing object with id ${id}`); }
            if (postProcessing.outputId === undefined) { throw new Error(`Could not find parent output for post-processing object with id ${id}`); }

            imagePostProcessingAdapter.updateOne(state.imagePostProcessing, { id: id, changes: { restoreNonHumanAnatomy: isEnabled } });

            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.PostProcessing);
        },


        /**
         * Performs an undo operation for all image customization data, reverting all image customization entities
         * to their original values from backend. Any new unsaved entries are lost.
         */
        allCustomizationChangesReverted(state) {
            modelAdapter.setAll(state.models, state.originalModels.entities as Record<string, Model>);
            customizationBaseAdapter.setAll(state.customizationBases, state.originalCustomizationBases.entities as Record<string, CustomizationBase>);
            imageOutputAdapter.setAll(state.imageOutputs, state.originalImageOutputs.entities as Record<string, ImageCustomizationOutput>);
            outputMetadataAdapter.setAll(state.outputMetadata, state.originalOutputMetadata.entities as Record<string, OutputMetadataItem>);

            imageDicomRestrictionAdapter.setAll(state.imageDicomRestrictions, state.originalImageDicomRestrictions.entities as Record<string, ImageDicomRestriction>);
            imageOutputGeometryAdapter.setAll(state.imageOutputGeometry, state.originalImageOutputGeometry.entities as Record<string, ImageOutputGeometry>);
            imageContourGenerationAdapter.setAll(state.imageContourGeneration, state.originalImageContourGeneration.entities as Record<string, ImageContourGeneration>);
            imagePostProcessingAdapter.setAll(state.imagePostProcessing, state.originalImagePostProcessing.entities as Record<string, ImagePostProcessing>);
            imageDicomTagAdapter.setAll(state.imageDicomTags, state.originalImageDicomTags.entities as Record<string, ImageDicomTag>);
            keepLargestComponentAdapter.setAll(state.keepLargestComponent, state.originalKeepLargestComponent.entities as Record<string, KeepLargestComponent>);
            fillHolesAdapter.setAll(state.fillHoles, state.originalFillHoles.entities as Record<string, FillHoles>);
            bodyMaskAdapter.setAll(state.bodyMasks, state.originalBodyMasks.entities as Record<string, BodyMask>);
            holeMaskAdapter.setAll(state.holeMasks, state.originalHoleMasks.entities as Record<string, HoleMask>);

            aeTitleRuleAdapter.setAll(state.aeTitleRules, state.originalAeTitleRules.entities as Record<string, AeTitleRule>);
            dicomRuleAdapter.setAll(state.dicomRules, state.originalDicomRules.entities as Record<string, DicomRule>);
            dicomAttributeRuleAdapter.setAll(state.dicomAttributeRules, state.originalDicomAttributeRules.entities as Record<string, DicomAttributeRule>);

            // remove any validation errors
            backendValidationErrorAdapter.removeAll(state.backendValidationErrors);
            sliceWrapper.clearSaveErrors(state);
        },

        /**
         * Signals that entire image model customization should be saved to backend.
         * @param action Current configuration target (AppClient).
         */
        modelCustomizationSaved(state, action: PayloadAction<{ configurationTarget: MVisionAppClient, force?: boolean }>) {
            // this is an empty action and is only used for signalling in sagas
        },

        /**
         * Marks image customization save as being in progress.
         */
        saveModelCustomizationStarted(state) {
            state.isModelCustomizationSavingInProgress = true;
            state.concurrentSaveError = null;
        },

        /**
         * Marks image customization save as having been finished, whether in success or failure.
         * @param action.saveWasSuccessful True if save succeeded, false otherwise.
         * @param action.errorMessage An optional error message if save was unsuccessful,
         * @param action.error An optional backend validation error object if save was unsuccessful.
         * @param action.concurrentSaveError An optional concurrent save error if the save failed due to a conflict.
         * @param action.updatedTimestamps An optional record of model name to updated timestamp, for updating lastModifiedTimestamp on successful save.
         */
        saveModelCustomizationFinished(state, action: PayloadAction<{ saveWasSuccessful: boolean, errorMessage?: string, error?: BackendValidationError, concurrentSaveError?: ConcurrentSaveError, updatedTimestamps?: Record<string, number> }>) {
            const { saveWasSuccessful, errorMessage, error, concurrentSaveError, updatedTimestamps } = action.payload;
            state.isModelCustomizationSavingInProgress = false;

            // remove existing backend validation errors -- we may add new ones more in just a moment
            // (DON'T remove FORM validation errors though -- we may have warnings there that still apply)
            backendValidationErrorAdapter.removeAll(state.backendValidationErrors);

            if (saveWasSuccessful) {
                // set everything back to unmodified & store as the new original entities
                Object.values(state.models.entities).forEach(m => m.isModified = false);
                Object.values(state.customizationBases.entities).forEach(c => c.isModified = false);
                Object.values(state.imageOutputs.entities).forEach(c => c.isModified = false);
                Object.values(state.outputMetadata.entities).forEach(md => md.isModified = false);

                Object.values(state.imageDicomRestrictions.entities).forEach(r => r.isModified = false);
                Object.values(state.imageOutputGeometry.entities).forEach(r => r.isModified = false);
                Object.values(state.imageContourGeneration.entities).forEach(r => r.isModified = false);
                Object.values(state.imagePostProcessing.entities).forEach(r => r.isModified = false);
                Object.values(state.imageDicomTags.entities).forEach(r => r.isModified = false);
                Object.values(state.keepLargestComponent.entities).forEach(r => r.isModified = false);
                Object.values(state.fillHoles.entities).forEach(r => r.isModified = false);
                Object.values(state.bodyMasks.entities).forEach(r => r.isModified = false);
                Object.values(state.holeMasks.entities).forEach(r => r.isModified = false);

                Object.values(state.aeTitleRules.entities).forEach(r => { r.isModified = false; r.isNew = false; });
                Object.values(state.dicomRules.entities).forEach(r => { r.isModified = false; r.isNew = false; });
                Object.values(state.dicomAttributeRules.entities).forEach(r => { r.isModified = false; r.isNew = false; });

                modelAdapter.setAll(state.originalModels, state.models.entities as Record<string, Model>);
                customizationBaseAdapter.setAll(state.originalCustomizationBases, state.customizationBases.entities as Record<string, CustomizationBase>);
                imageOutputAdapter.setAll(state.originalImageOutputs, state.imageOutputs.entities as Record<string, ImageCustomizationOutput>);
                outputMetadataAdapter.setAll(state.originalOutputMetadata, state.outputMetadata.entities as Record<string, OutputMetadataItem>);

                imageDicomRestrictionAdapter.setAll(state.originalImageDicomRestrictions, state.imageDicomRestrictions.entities as Record<string, ImageDicomRestriction>);
                imageOutputGeometryAdapter.setAll(state.originalImageOutputGeometry, state.imageOutputGeometry.entities as Record<string, ImageOutputGeometry>);
                imageContourGenerationAdapter.setAll(state.originalImageContourGeneration, state.imageContourGeneration.entities as Record<string, ImageContourGeneration>);
                imagePostProcessingAdapter.setAll(state.originalImagePostProcessing, state.imagePostProcessing.entities as Record<string, ImagePostProcessing>);
                imageDicomTagAdapter.setAll(state.originalImageDicomTags, state.imageDicomTags.entities as Record<string, ImageDicomTag>);
                keepLargestComponentAdapter.setAll(state.originalKeepLargestComponent, state.keepLargestComponent.entities as Record<string, KeepLargestComponent>);
                fillHolesAdapter.setAll(state.originalFillHoles, state.fillHoles.entities as Record<string, FillHoles>);
                bodyMaskAdapter.setAll(state.originalBodyMasks, state.bodyMasks.entities as Record<string, BodyMask>);
                holeMaskAdapter.setAll(state.originalHoleMasks, state.holeMasks.entities as Record<string, HoleMask>);

                aeTitleRuleAdapter.setAll(state.originalAeTitleRules, state.aeTitleRules.entities as Record<string, AeTitleRule>);
                dicomRuleAdapter.setAll(state.originalDicomRules, state.dicomRules.entities as Record<string, DicomRule>);
                dicomAttributeRuleAdapter.setAll(state.originalDicomAttributeRules, state.dicomAttributeRules.entities as Record<string, DicomAttributeRule>);

                // update lastModifiedTimestamp on models & originalModels from backend response
                updateModelTimestampsAfterSave(state, updatedTimestamps);
            }

            state.modelCustomizationSaveError = errorMessage !== undefined ? errorMessage : null;
            state.concurrentSaveError = concurrentSaveError ?? null;

            // collect save errors
            sliceWrapper.collectSaveErrors(state, saveWasSuccessful, error);
        },

        /**
         * Duplicates given customization base and any entities under it.
         * @param action.customizationBaseId The ID of the customization base to duplicate.
         * @param action.newCustomizationName The name of the new duplicate customization base.
         */
        customizationBaseDuplicated(state, action: PayloadAction<{ customizationBaseId: string, newCustomizationName: string }>) {
            // this is an empty action and is only used for signalling in sagas
        },

        /**
        * Adds duplicated customization base items into redux store. Duplication itself is done in a saga function.
        */
        customizationDuplicationItemsAdded(state, action: PayloadAction<{
            parentModelId: string,
            customization: CustomizationBase,
            outputs: ImageCustomizationOutput[],
            metadata: OutputMetadataItem[],

            imageDicomRestrictions: ImageDicomRestriction[],
            imageOutputGeometry: ImageOutputGeometry[],
            imageContourGeneration: ImageContourGeneration[],
            imagePostProcessing: ImagePostProcessing[],
            imageDicomTags: ImageDicomTag[],
            keepLargestComponent: KeepLargestComponent[],
            fillHoles: FillHoles[],
            bodyMasks: BodyMask[],
            holeMasks: HoleMask[],

            duplicatedIds: DuplicatedIdMap[]
        }>) {
            const { parentModelId, customization, outputs, metadata,
                imageDicomRestrictions, imageOutputGeometry, imageContourGeneration, imagePostProcessing,
                imageDicomTags, keepLargestComponent, fillHoles, bodyMasks, holeMasks,
                duplicatedIds } = action.payload;

            const parentModel = state.models.entities[parentModelId];
            if (!parentModel) { throw new Error('No parent model found for duplication target'); }

            customizationBaseAdapter.addOne(state.customizationBases, customization);
            imageOutputAdapter.addMany(state.imageOutputs, outputs);
            outputMetadataAdapter.addMany(state.outputMetadata, metadata);
            imageDicomRestrictionAdapter.addMany(state.imageDicomRestrictions, imageDicomRestrictions);
            imageOutputGeometryAdapter.addMany(state.imageOutputGeometry, imageOutputGeometry);
            imageContourGenerationAdapter.addMany(state.imageContourGeneration, imageContourGeneration);
            imagePostProcessingAdapter.addMany(state.imagePostProcessing, imagePostProcessing);
            imageDicomTagAdapter.addMany(state.imageDicomTags, imageDicomTags);
            keepLargestComponentAdapter.addMany(state.keepLargestComponent, keepLargestComponent);
            fillHolesAdapter.addMany(state.fillHoles, fillHoles);
            bodyMaskAdapter.addMany(state.bodyMasks, bodyMasks);
            holeMaskAdapter.addMany(state.holeMasks, holeMasks);

            modelAdapter.updateOne(state.models, {
                id: parentModelId,
                changes: { customizations: parentModel.customizations.concat(customization.id) }
            });

            setObjectAndAncestorsAsModified(state, [parentModelId], CustomizationObjectType.Model);
        },

        /**
         * Removes the specified customization base entity and all entities under it in the hierarchy.
         * @param action The ID of the customization base to remove.
         */
        customizationBaseRemoved(state, action: PayloadAction<string>) {
            const customizationBaseId = action.payload;

            sliceWrapper.removeCustomizationBase(state, customizationBaseId);
        },

        /**
         * Signals that all current image customizations should be reset back to factory default values.
         * @param action Current configuration target
         */
        allModelCustomizationsReset(state, action: PayloadAction<MVisionAppClient>) {
            // this is an empty action and is only used for signalling in sagas
        },

        /** Marks image customization reset as having started. */
        resetAllModelCustomizationsStarted(state) {
            state.isAllModelCustomizationsResetInProgress = true;
        },

        /** Marks model customization reset as finished. 
         * @param action.resetWasSuccessful True if reset was successful, false otherwise.
         * @param action.errorMessage Optional error message if output reset failed.
         */
        resetAllModelCustomizationsFinished(state, action: PayloadAction<{ resetWasSuccessful: boolean, errorMessage?: string }>) {
            const { resetWasSuccessful, errorMessage } = action.payload;

            sliceWrapper.finishResetAllModelCustomizations(state, resetWasSuccessful, errorMessage);
        },

        /** Resets the customization import export error. */
        resetCustomizationImportExportError(state) {
            state.customizationImportExportError = null;
        },

        /**
         * Signals that a single customization base should be reset back to factory default values.
         * @param action Internal ID of the customization base to reset.
         */
        singleCustomizationReset(state, action: PayloadAction<string>) {
            // this is an empty action and is only used for signalling in sagas
        },

        /** Marks a single image output reset as having started. */
        resetSingleOutputStarted(state) {
            state.isOutputResetInProgress = true;
        },

        /** Marks a single image output reset as finished.
         * @param action.resetWasSuccessful True if reset was succesful, false otherwise.
         * @param action.errorMessage Optional error message if output reset failed.
         */
        resetSingleOutputFinished(state, action: PayloadAction<{ resetWasSuccessful: boolean, errorMessage?: string }>) {
            state.isOutputResetInProgress = false;
            state.modelCustomizationDataError = action.payload.errorMessage || null;
        },

        /**
         * Replaces a specified image customization output with supplied values, including ROIs, targets and metadata. ID of the original 
         * image output is retained.
         * @param action.customizationOutputId The ID of the image output to be replaced.
         * @param action.newOutputEntities The values that will be used to replace the image output.
         */
        customizationOutputReplaced(state, action: PayloadAction<{ customizationOutputId: string, newOutputEntities: ImageCustomizationOutputEntities }>) {
            const { customizationOutputId, newOutputEntities } = action.payload;
            const originalOutput = state.imageOutputs.entities[customizationOutputId];
            const originalDicomRestriction = state.imageDicomRestrictions.entities[originalOutput.dicomRestriction];
            const originalOutputGeometry = state.imageOutputGeometry.entities[originalOutput.outputGeometry];
            const originalContourGeneration = state.imageContourGeneration.entities[originalOutput.contourGeneration];
            const originalPostProcessing = state.imagePostProcessing.entities[originalOutput.postProcessing];
            const originalDicomTags = originalDicomRestriction.tags.map(tId => state.imageDicomTags.entities[tId]);
            const originalKeepLargestComponent = state.keepLargestComponent.entities[originalPostProcessing.keepLargestComponent];
            const originalFillHoles = state.fillHoles.entities[originalPostProcessing.fillHoles];
            const originalBodyMask = state.bodyMasks.entities[originalFillHoles.bodyMask];
            const originalHoleMask = state.holeMasks.entities[originalFillHoles.holeMask];

            if (!originalOutput) { throw new Error(`No original image output (${customizationOutputId}) to replace!`); }
            if (!originalDicomRestriction) { throw new Error(`No original dicom restriction (${originalOutput.dicomRestriction}) to replace!`); }
            if (!originalOutputGeometry) { throw new Error(`No original output geometry (${originalOutput.outputGeometry}) to replace!`); }
            if (!originalContourGeneration) { throw new Error(`No original contour generation (${originalOutput.contourGeneration}) to replace!`); }
            if (!originalPostProcessing) { throw new Error(`No original post-processing (${originalOutput.postProcessing}) to replace!`); }
            if (!originalKeepLargestComponent) { throw new Error(`No original keep largest component (${originalPostProcessing.keepLargestComponent}) to replace!`); }
            if (!originalFillHoles) { throw new Error(`No original fill holes (${originalPostProcessing.fillHoles}) to replace!`); }
            if (!originalBodyMask) { throw new Error(`No original body mask (${originalFillHoles.bodyMask}) to replace!`); }
            if (!originalHoleMask) { throw new Error(`No original hole mask (${originalFillHoles.holeMask}) to replace!`); }
            if (newOutputEntities.outputs.length < 1) { throw new Error('No replacement image output given'); }

            // currently only support replacing first output from given input even if we'd get several
            const newOutput = newOutputEntities.outputs[0];
            const newMetadata = newOutputEntities.metadata.filter(m => newOutput.metadata.includes(m.id));

            const newDicomRestriction = newOutputEntities.dicomRestrictions.find(d => newOutput.dicomRestriction === d.id);
            const newOutputGeometry = newOutputEntities.outputGeometry.find(o => newOutput.outputGeometry === o.id);
            const newContourGeneration = newOutputEntities.contourGeneration.find(c => newOutput.contourGeneration === c.id);
            const newPostProcessing = newOutputEntities.postProcessing.find(p => newOutput.postProcessing === p.id);
            assertIsDefined(newDicomRestriction, 'No matching dicom restriction found');
            assertIsDefined(newOutputGeometry, 'No matching output geometry found');
            assertIsDefined(newContourGeneration, 'No matching contour generation found');
            assertIsDefined(newPostProcessing, 'No matching post-processing found');

            const newDicomTags = newOutputEntities.dicomTags.filter(t => newDicomRestriction.tags.includes(t.id));
            const newKeepLargestComponents = newOutputEntities.keepLargestComponent.find(k => newPostProcessing.keepLargestComponent === k.id);
            const newFillHoles = newOutputEntities.fillHoles.find(f => newPostProcessing.fillHoles === f.id);
            assertIsDefined(newKeepLargestComponents, 'No matching keep largest component found');
            assertIsDefined(newFillHoles, 'No matching fill holes found');

            const newBodyMask = newOutputEntities.bodyMasks.find(b => newFillHoles.bodyMask === b.id);
            const newHoleMask = newOutputEntities.holeMasks.find(h => newFillHoles.holeMask === h.id);
            assertIsDefined(newBodyMask, 'No matching body mask found');
            assertIsDefined(newHoleMask, 'No matching hole mask found');

            // modify key parts of the replacement output items to match the original. also mark everything as modified
            newOutput.id = originalOutput.id;
            newOutput.modelCustomizationBaseId = originalOutput.modelCustomizationBaseId;
            newOutput.filename = originalOutput.filename;
            newOutput.isModified = true;

            newMetadata.forEach(m => {
                if (m.attribute === METADATA_FILENAME_ATTRIBUTE) { m.value = newOutput.filename; }
                m.modelCustomizationOutputId = newOutput.id;
                m.isModified = true;
            });

            newDicomRestriction.outputId = newOutput.id;
            newDicomRestriction.isModified = true;
            newOutputGeometry.outputId = newOutput.id;
            newOutputGeometry.isModified = true;
            newContourGeneration.outputId = newOutput.id;
            newContourGeneration.isModified = true;
            newPostProcessing.outputId = newOutput.id;
            newPostProcessing.isModified = true;

            newDicomTags.forEach(t => {
                // t.dicomRestrictionId = newDicomRestriction.id;
                t.isModified = true;
            });
            newKeepLargestComponents.isModified = true;
            newFillHoles.isModified = true;
            newBodyMask.isModified = true;
            newHoleMask.isModified = true;


            // remove old items and related form validation errors
            outputMetadataAdapter.removeMany(state.outputMetadata, originalOutput.metadata);
            imageDicomRestrictionAdapter.removeOne(state.imageDicomRestrictions, originalDicomRestriction.id);
            imageOutputGeometryAdapter.removeOne(state.imageOutputGeometry, originalOutputGeometry.id);
            imageContourGenerationAdapter.removeOne(state.imageContourGeneration, originalContourGeneration.id);
            imagePostProcessingAdapter.removeOne(state.imagePostProcessing, originalPostProcessing.id);
            imageDicomTagAdapter.removeMany(state.imageDicomTags, originalDicomTags.map(t => t.id));
            keepLargestComponentAdapter.removeOne(state.keepLargestComponent, originalKeepLargestComponent.id);
            fillHolesAdapter.removeOne(state.fillHoles, originalFillHoles.id);
            bodyMaskAdapter.removeOne(state.bodyMasks, originalBodyMask.id);
            holeMaskAdapter.removeOne(state.holeMasks, originalHoleMask.id);

            // add new items
            outputMetadataAdapter.addMany(state.outputMetadata, newMetadata);
            imageDicomRestrictionAdapter.addOne(state.imageDicomRestrictions, newDicomRestriction);
            imageOutputGeometryAdapter.addOne(state.imageOutputGeometry, newOutputGeometry);
            imageContourGenerationAdapter.addOne(state.imageContourGeneration, newContourGeneration);
            imagePostProcessingAdapter.addOne(state.imagePostProcessing, newPostProcessing);
            imageDicomTagAdapter.addMany(state.imageDicomTags, newDicomTags);
            keepLargestComponentAdapter.addOne(state.keepLargestComponent, newKeepLargestComponents);
            fillHolesAdapter.addOne(state.fillHoles, newFillHoles);
            bodyMaskAdapter.addOne(state.bodyMasks, newBodyMask);
            holeMaskAdapter.addOne(state.holeMasks, newHoleMask);

            // swap in the new customization output
            imageOutputAdapter.setOne(state.imageOutputs, newOutput);

            // mark ancestors as modified (start with customization base as the output was already marked earlier)
            setObjectAndAncestorsAsModified(state, [newOutput.modelCustomizationBaseId], CustomizationObjectType.CustomizationBase);
        },

        /**
         * Renames given customization base.
         * @param action.customizationBaseId ID of the customization base to rename.
         * @param action.name The new name of the customization base.
         */
        customizationRenamed(state, action: PayloadAction<{ customizationBaseId: string, name: string }>) {
            const { customizationBaseId, name } = action.payload;
            customizationBaseAdapter.updateOne(state.customizationBases, { id: customizationBaseId, changes: { isModified: true, customizationName: name } });
            setObjectAndAncestorsAsModified(state, [customizationBaseId], CustomizationObjectType.CustomizationBase);
        },


        /**
         * Export entire current image customization into a JSON file.
         * @param action Current configuration target.
         */
        modelCustomizationExported(state, action: PayloadAction<MVisionAppClient | undefined>) {
            // this is an empty action and is only used for signalling in sagas
        },

        /**
         * Imports parsed JSON file contents into current image configuration target.
         * @param action Image customization to import.
         */
        modelCustomizationImported(state, action: PayloadAction<ImageCustomizationEntities | null>) {
            // this is an empty action and is only used for signalling in sagas
        },

        /**
         * Marks importing of a JSON file into image customization as failed.
         * @param action Error message, or null if error message should be reset.
         */
        modelCustomizationImportFailed(state, action: PayloadAction<string | null>) {
            const errorMessage = action.payload;
            state.customizationImportExportError = errorMessage;
        },

        /**
         * Marks exporting of a JSON file from image customization as failed.
         * @param action Error message, or null if error message should be reset.
         */
        modelCustomizationExportFailed(state, action: PayloadAction<string | null>) {
            const errorMessage = action.payload;
            state.customizationImportExportError = errorMessage;
        },

        /**
         * Export image customization as a PDF file via backend API.
         * @param action Current configuration target.
         */
        modelCustomizationPdfExportRequested(_state, _action: PayloadAction<MVisionAppClient | undefined>) {
            // Signal-only reducer — state is set in the saga via pdfExportStarted
        },

        /** Marks PDF export as started. */
        pdfExportStarted(state) {
            state.pdfExportInProgress = true;
            state.customizationImportExportError = null;
        },

        /** Marks PDF export as finished. */
        pdfExportFinished(state) {
            state.pdfExportInProgress = false;
        },

        /**
         * Marks any supplied image customization entities as having been modified.
         * @param action A collection of customization entities to mark as modified as per their
         * entity adapter IDs.
         */
        modelCustomizationObjectsSetAsModified(state, action: PayloadAction<Partial<ImageCustomizationEntities>>) {
            const entitiesToUpdate = action.payload;

            sliceWrapper.setStateObjectsAsModified(state, entitiesToUpdate);
        },

        /**
         * Toggles a tag on or off from active tag filters.
         * @param action label of the tag to toggle.
         */
        activeTagFilterToggled(state, action: PayloadAction<string>) {
            const tag = action.payload;
            sliceWrapper.toggleActiveTagFilter(state, tag);
        },

        /**
         * Resets (unselects) all current active tag filters.
         */
        activeTagFiltersReset(state) {
            sliceWrapper.resetActiveTagFilters(state);
        },

        /**
         * Toggles a tag on or off from active tag filters.
         * @param action label of the tag to toggle.
         */
        activeVersionTagFilterToggled(state, action: PayloadAction<string>) {
            const tag = action.payload;
            sliceWrapper.toggleActiveVersionTagFilter(state, tag);
        },

        /**
         * Resets (unselects) all current active version tag filters.
         */
        activeVersionTagFiltersReset(state) {
            sliceWrapper.resetActiveVersionTagFilters(state);
        },

        /** Toggles a modality on or off from active modality filters for the given direction (input/output) and modality. */
        activeModalityFilterToggled(state, action: PayloadAction<{ direction: ModalityDirection, modality: Modality }>) {
            const { direction, modality } = action.payload;
            sliceWrapper.toggleActiveModalityFilter(state, direction, modality);
        },

        /** Resets (unselects) all current active modality filters for the given direction (input/output). */
        activeModalityFiltersReset(state, action: PayloadAction<ModalityDirection>) {
            const direction = action.payload;
            sliceWrapper.resetActiveModalityFilters(state, direction);
        },

        /** Sets the active free-text filter value. */
        activeFreeTextFilterSet(state, action: PayloadAction<string>) {
            const value = action.payload;
            sliceWrapper.setActiveFreeTextFilter(state, value);
        },

        /** Resets the active free-text filter to an empty string. */
        activeFreeTextFilterReset(state) {
            sliceWrapper.resetActiveFreeTextFilter(state);
        },

        /** Sets whether deprecated models are shown. */
        deprecatedModelsVisibilitySet(state, action: PayloadAction<boolean>) {
            sliceWrapper.setAreDeprecatedModelsShown(state, action.payload);
        },

        /** Sets whether non-licensed models are shown. */
        nonLicensedModelsVisibilitySet(state, action: PayloadAction<boolean>) {
            sliceWrapper.setAreNonLicensedModelsShown(state, action.payload);
        },

        /** Sets whether user-hidden models are shown. */
        userHiddenModelsVisibilitySet(state, action: PayloadAction<boolean>) {
            sliceWrapper.setAreUserHiddenModelsShown(state, action.payload);
        },

        /** Unit test helper reducer for setting an object and all its ancestors as modified.
         *
         * Do not use this reducer outside of unit tests!
         */
        _unitTest_customizationObjectPathSetAsModified(state, action: PayloadAction<{ objectIds: string[], objectType: CustomizationObjectType }>) {
            const { objectIds, objectType } = action.payload;
            setObjectAndAncestorsAsModified(state, objectIds, objectType);
        },

    },
    selectors: {

        /** Returns all tags that are currently used for filtering */
        selectActiveTags: (state): string[] => state.activeTagFilters,

        /** Returns all active input modality filters */
        selectActiveInputModalities: (state): Modality[] => state.activeInputModalityFilters,

        /** Returns all active output modality filters */
        selectActiveOutputModalities: (state): Modality[] => state.activeOutputModalityFilters,

        /** Returns all active version tags */
        selectActiveVersionTags: (state): string[] => state.activeVersionTags,

        /** Returns the active free-text filter value */
        selectActiveFreeTextFilter: (state): string => state.activeFreeTextFilter,

        /** Returns whether deprecated models are shown */
        selectAreDeprecatedModelsShown: (state): boolean => state.areDeprecatedModelsShown,

        /** Returns whether non-licensed models are shown */
        selectAreNonLicensedModelsShown: (state): boolean => state.areNonLicensedModelsShown,

        /** Returns whether user-hidden models are shown */
        selectAreUserHiddenModelsShown: (state): boolean => state.areUserHiddenModelsShown,

        /** Returns the top-level model name for a image+ output */
        selectModelNameForOutput: (state, outputId: string) => {
            const output = localSelectors.selectOutputById(state, outputId);
            if (output) {
                const customization = localSelectors.selectCustomizationBaseById(state, output.modelCustomizationBaseId);
                if (customization) {
                    const model = localSelectors.selectModelById(state, customization.modelId);
                    if (model) {
                        return model.modelName;
                    }
                }
            }
        },

        /** Returns true if any model customization object has been modified. */
        selectIsAnyCustomizationModelModified: createSelector(
            (state: ImageSliceState): Model[] => localSelectors.selectModels(state),
            (models) => models.some(m => m.isModified)),

        /** Returns an array of all customization names and their internal parent model IDs. */
        selectAllCustomizationNamesAndParentModelIds: createSelector(
            (state: ImageSliceState): CustomizationBase[] => localSelectors.selectCustomizationBases(state),
            (customizations) => customizations.map(c => ({ customizationName: c.customizationName, modelId: c.modelId }))),

        /** Returns true if there are ANY unsaved IMAGE+ changes in the app. */
        selectAnyUnsavedImageChangesInApp: (state) => localSelectors.selectIsAnyCustomizationModelModified(state),

        /** Returns dicom restriction object for given image+ customization output id, or undefined if output or 
         * dicom restriction is not found. */
        selectDicomRestrictionForOutput: createSelector(
            [
                (state: ImageSliceState, outputId: string): ImageCustomizationOutput | undefined => localSelectors.selectOutputById(state, outputId),
                (state: ImageSliceState): Record<string, ImageDicomRestriction> => localSelectors.selectDicomRestrictionEntities(state),

            ],
            (output, dicomRestrictionEntities) => output ? dicomRestrictionEntities[output.dicomRestriction] : undefined),

        /** Returns output geometry object for given image+ customization output id, or undefined if output or 
         * output geometry is not found. */
        selectOutputGeometryForOutput: createSelector(
            [
                (state: ImageSliceState, outputId: string): ImageCustomizationOutput | undefined => localSelectors.selectOutputById(state, outputId),
                (state: ImageSliceState): Record<string, ImageOutputGeometry> => localSelectors.selectImageOutputGeometryEntities(state),

            ],
            (output, geometryEntities) => output ? geometryEntities[output.outputGeometry] : undefined),

        /** Returns contour generation object for given image+ customization output id, or undefined if output or
         * contour generation is not found. */
        selectContourGenerationForOutput: createSelector(
            [
                (state: ImageSliceState, outputId: string): ImageCustomizationOutput | undefined => localSelectors.selectOutputById(state, outputId),
                (state: ImageSliceState): Record<string, ImageContourGeneration> => localSelectors.selectImageContourGenerationEntities(state),

            ],
            (output, contourEntities) => output ? contourEntities[output.contourGeneration] : undefined),

        /** Returns post-processing object for given image+ customization output id, or undefined if output or
         * post-processing is not found. */
        selectPostProcessingForOutput: createSelector(
            [
                (state: ImageSliceState, outputId: string): ImageCustomizationOutput | undefined => localSelectors.selectOutputById(state, outputId),
                (state: ImageSliceState): Record<string, ImagePostProcessing> => localSelectors.selectImagePostProcessingEntities(state),

            ],
            (output, postProcessingEntities) => output ? postProcessingEntities[output.postProcessing] : undefined),

        /** Returns dicom restriction, output geometry, contour generation and post-processing configuration objects for given image+ customization
         * output id, or undefined if not found. */
        selectMainConfigurationObjectsForOutput: createSelector(
            [
                (state: ImageSliceState, outputId: string): ImageDicomRestriction | undefined => localSelectors.selectDicomRestrictionForOutput(state, outputId),
                (state: ImageSliceState, outputId: string): ImageOutputGeometry | undefined => localSelectors.selectOutputGeometryForOutput(state, outputId),
                (state: ImageSliceState, outputId: string): ImageContourGeneration | undefined => localSelectors.selectContourGenerationForOutput(state, outputId),
                (state: ImageSliceState, outputId: string): ImagePostProcessing | undefined => localSelectors.selectPostProcessingForOutput(state, outputId),
            ],
            (dicomRestriction, outputGeometry, contourGeneration, postProcessing) => {
                return {
                    dicomRestriction,
                    outputGeometry,
                    contourGeneration,
                    postProcessing
                }
            }
        ),

        selectPostProcessingObjectsForOutput: createSelector(
            [
                (state: ImageSliceState, outputId: string): ImagePostProcessing | undefined => localSelectors.selectPostProcessingForOutput(state, outputId),
                (state: ImageSliceState): Record<string, KeepLargestComponent> => localSelectors.selectKeepLargestComponentEntities(state),
                (state: ImageSliceState): Record<string, FillHoles> => localSelectors.selectFillHolesEntities(state),
                (state: ImageSliceState): Record<string, BodyMask> => localSelectors.selectBodyMaskEntities(state),
                (state: ImageSliceState): Record<string, HoleMask> => localSelectors.selectHoleMaskEntities(state),
            ],
            (
                postProcessing,
                keepLargestComponentEntities,
                fillHolesEntities,
                bodyMaskEntities,
                holeMaskEntities) => {
                const fillHoles = postProcessing ? fillHolesEntities[postProcessing.fillHoles] : undefined;
                return {
                    postProcessing,
                    keepLargestComponent: postProcessing ? keepLargestComponentEntities[postProcessing.keepLargestComponent] : undefined,
                    fillHoles: postProcessing ? fillHolesEntities[postProcessing.fillHoles] : undefined,
                    bodyMask: fillHoles ? bodyMaskEntities[fillHoles.bodyMask] : undefined,
                    holeMask: fillHoles ? holeMaskEntities[fillHoles.holeMask] : undefined,
                }
            }
        ),

        /** Returns true if requested model is an MR model, false otherwise or if an error occurred.
         * TODO: this is a temporary solution until we have a better way to determine if a model is MR or not.
         */
        selectIsMRModelForOutput: createSelector(
            [
                (state: ImageSliceState, outputId: string | undefined): ImageCustomizationOutput | undefined => outputId ? localSelectors.selectOutputById(state, outputId) : undefined,
                (state: ImageSliceState): Record<string, CustomizationBase> => localSelectors.selectCustomizationBaseEntities(state),
                (state: ImageSliceState): Record<string, Model> => localSelectors.selectModelEntities(state),
            ],
            (
                output,
                customizations,
                models
            ) => {
                if (output) {
                    const customization = customizations[output?.modelCustomizationBaseId];
                    if (customization) {
                        const model = models[customization.modelId];
                        if (model) {
                            // TODO: this is a temporary solution until we have a better way to determine if a model is MR or not.
                            const splitModelLabel = model.label.split('->');
                            if (splitModelLabel.length > 0) {
                                return splitModelLabel[0].includes('MR');
                            }
                        }
                    }
                }

                return false;
            }
        ),

        // ENTITY ADAPTER SELECTORS:
        // for all supported createEntityAdapter selectors see https://redux-toolkit.js.org/api/createEntityAdapter#selector-functions

        selectModels: (state): Model[] => modelAdapterSelectors.selectAll(state),
        selectModelEntities: (state) => modelAdapterSelectors.selectEntities(state),
        selectModelById: (state, id: string): Model | undefined => modelAdapterSelectors.selectById(state, id),

        selectCustomizationBases: (state) => customizationBaseAdapterSelectors.selectAll(state),
        selectCustomizationBaseIds: (state) => customizationBaseAdapterSelectors.selectIds(state),
        selectCustomizationBaseById: (state, id: string): CustomizationBase | undefined => customizationBaseAdapterSelectors.selectById(state, id),
        selectCustomizationBaseEntities: (state) => customizationBaseAdapterSelectors.selectEntities(state),
        selectCustomizationBasesInAlphabeticalOrder: createSelector(
            [(state: ImageSliceState): CustomizationBase[] => localSelectors.selectCustomizationBases(state)],
            bases => naturalSort(bases, 'customizationName')),

        selectOutputs: (state) => imageOutputAdapterSelectors.selectAll(state),
        selectOutputIds: (state) => imageOutputAdapterSelectors.selectIds(state),
        selectOutputEntities: (state) => imageOutputAdapterSelectors.selectEntities(state),
        selectOutputById: (state, id: string): ImageCustomizationOutput | undefined => imageOutputAdapterSelectors.selectById(state, id),


        selectOutputMetadata: (state) => outputMetadataAdapterSelectors.selectAll(state),
        selectOutputMetadataEntities: (state) => outputMetadataAdapterSelectors.selectEntities(state),
        selectOriginalOutputMetadataEntities: (state) => originalOutputMetadataAdapterSelectors.selectEntities(state),
        selectOutputMetadataById: (state, id: string): OutputMetadataItem | undefined => outputMetadataAdapterSelectors.selectById(state, id),

        selectAeTitleRules: (state) => aeTitleRuleAdapterSelectors.selectAll(state),
        selectAeTitleRuleEntities: (state) => aeTitleRuleAdapterSelectors.selectEntities(state),
        selectAeTitleRuleById: (state, id: string): AeTitleRule | undefined => aeTitleRuleAdapterSelectors.selectById(state, id),

        selectDicomRules: (state) => dicomRuleAdapterSelectors.selectAll(state),
        selectDicomRuleEntities: (state) => dicomRuleAdapterSelectors.selectEntities(state),
        selectDicomRuleById: (state, id: string): DicomRule | undefined => dicomRuleAdapterSelectors.selectById(state, id),

        selectDicomAttributeRules: (state) => dicomAttributeRuleAdapterSelectors.selectAll(state),
        selectDicomAttributeRuleById: (state, id: string): DicomAttributeRule | undefined => dicomAttributeRuleAdapterSelectors.selectById(state, id),

        selectCustomizationValidationError: (state, id: string): BackendValidationErrorViewModel | undefined => customizationValidationErrorAdapterSelectors.selectById(state, id),
        selectCustomizationValidationErrorEntities: (state) => customizationValidationErrorAdapterSelectors.selectEntities(state),

        /** Form validation errors for the Image+ slice — derived on demand from current entity state.
         *  Image+ has only trigger-rule validation (DICOM rule / DICOM attribute / AE title uniqueness);
         *  no per-output rules currently exist for this slice. */
        selectFormValidationErrors: createSelector(
            [
                (state: ImageSliceState): DicomRule[] => localSelectors.selectDicomRules(state),
                (state: ImageSliceState): DicomAttributeRule[] => localSelectors.selectDicomAttributeRules(state),
                (state: ImageSliceState): AeTitleRule[] => localSelectors.selectAeTitleRules(state),
            ],
            (dicomRules, dicomAttributeRules, aeTitleRules) =>
                composeTriggerValidationErrors(dicomRules, dicomAttributeRules, aeTitleRules)
        ),

        selectDicomRestrictions: (state) => imageDicomRestrictionAdapterSelectors.selectAll(state),
        selectDicomRestrictionEntities: (state) => imageDicomRestrictionAdapterSelectors.selectEntities(state),
        selectDicomRestrictionById: (state, id: string): ImageDicomRestriction | undefined => imageDicomRestrictionAdapterSelectors.selectById(state, id),

        selectImageOutputGeometry: (state) => imageOutputGeometryAdapterSelectors.selectAll(state),
        selectImageOutputGeometryEntities: (state) => imageOutputGeometryAdapterSelectors.selectEntities(state),
        selectImageOutputGeometryById: (state, id: string): ImageOutputGeometry | undefined => imageOutputGeometryAdapterSelectors.selectById(state, id),

        selectImageContourGeneration: (state) => imageContourGenerationAdapterSelectors.selectAll(state),
        selectImageContourGenerationEntities: (state) => imageContourGenerationAdapterSelectors.selectEntities(state),
        selectImageContourGenerationById: (state, id: string): ImageContourGeneration | undefined => imageContourGenerationAdapterSelectors.selectById(state, id),

        selectImagePostProcessing: (state) => imagePostProcessingAdapterSelectors.selectAll(state),
        selectImagePostProcessingEntities: (state) => imagePostProcessingAdapterSelectors.selectEntities(state),
        selectImagePostProcessingById: (state, id: string): ImagePostProcessing | undefined => imagePostProcessingAdapterSelectors.selectById(state, id),

        selectDicomTags: (state) => imageDicomTagAdapterSelectors.selectAll(state),
        selectDicomTagById: (state, id: string): ImageDicomTag | undefined => imageDicomTagAdapterSelectors.selectById(state, id),
        selectOriginalDicomTagById: (state, id: string): ImageDicomTag | undefined => originalImageDicomTagAdapterSelectors.selectById(state, id),

        selectKeepLargestComponent: (state) => keepLargestComponentAdapterSelectors.selectAll(state),
        selectKeepLargestComponentEntities: (state) => keepLargestComponentAdapterSelectors.selectEntities(state),
        selectKeepLargestComponentById: (state, id: string): KeepLargestComponent | undefined => keepLargestComponentAdapterSelectors.selectById(state, id),

        selectFillHoles: (state) => fillHolesAdapterSelectors.selectAll(state),
        selectFillHolesEntities: (state) => fillHolesAdapterSelectors.selectEntities(state),
        selectFillHolesById: (state, id: string): FillHoles | undefined => fillHolesAdapterSelectors.selectById(state, id),

        selectBodyMasks: (state) => bodyMaskAdapterSelectors.selectAll(state),
        selectBodyMaskEntities: (state) => bodyMaskAdapterSelectors.selectEntities(state),
        selectBodyMaskById: (state, id: string): BodyMask | undefined => bodyMaskAdapterSelectors.selectById(state, id),

        selectHoleMasks: (state) => holeMaskAdapterSelectors.selectAll(state),
        selectHoleMaskEntities: (state) => holeMaskAdapterSelectors.selectEntities(state),
        selectHoleMaskById: (state, id: string): HoleMask | undefined => holeMaskAdapterSelectors.selectById(state, id),
    }
});

export const {
    modelCustomizationsSet,
    modelVisibilityUpdated,
    modelCustomizationDescriptionUpdated,
    metadataItemUpdated,
    metadataItemRemoved,
    scrollToViewFromModelCustomizationMetadataRemoved,
    aeTitleRuleAdded,
    aeTitleRuleActionUpdated,
    aeTitleRuleRemoved,
    dicomRuleAdded,
    dicomRuleRemoved,
    dicomAttributeRuleAdded,
    dicomAttributeRuleRemoved,
    dicomAttributeRuleUpdated,

    imageDicomRestrictionEnabledSet,
    newDicomTagAdded,
    dicomTagAttributeSet,
    dicomTagValueSet,
    dicomTagRemoved,
    imageOutputGeometryMatchInputSet,
    imageOutputGeometryFovSizeSet,
    imageOutputGeometryMatrixSizeSet,
    imageOutputGeometryMatchInputSliceThicknessSet,
    imageOutputGeometryOutputSliceThicknessSet,
    imageContourGenerationEnabledSet,
    imageContourGenerationActionSet,
    imageContourGenerationAttachToSet,
    imageContourGenerationRoiPhysicalPropertiesEnabledSet,
    imageContourGenerationRoiPhysicalPropertiesDensityTypeSet,
    imageKeepLargestComponentEnabledSet,
    imageFillHolesEnabledSet,
    imageRestoreNonHumanAnatomyEnabledSet,

    allCustomizationChangesReverted,
    modelCustomizationSaved,
    saveModelCustomizationStarted,
    saveModelCustomizationFinished,
    customizationBaseDuplicated,
    customizationDuplicationItemsAdded,
    customizationBaseRemoved,
    allModelCustomizationsReset,
    resetAllModelCustomizationsStarted,
    resetAllModelCustomizationsFinished,
    resetCustomizationImportExportError,
    singleCustomizationReset,
    resetSingleOutputStarted,
    resetSingleOutputFinished,
    customizationOutputReplaced,
    customizationRenamed,

    modelCustomizationExported,
    modelCustomizationExportFailed,
    modelCustomizationPdfExportRequested,
    pdfExportStarted,
    pdfExportFinished,
    modelCustomizationImported,
    modelCustomizationImportFailed,
    modelCustomizationObjectsSetAsModified,
    activeTagFilterToggled,
    activeTagFiltersReset,
    activeVersionTagFilterToggled,
    activeVersionTagFiltersReset,
    activeModalityFilterToggled,
    activeModalityFiltersReset,
    activeFreeTextFilterSet,
    activeFreeTextFilterReset,
    deprecatedModelsVisibilitySet,
    nonLicensedModelsVisibilitySet,
    userHiddenModelsVisibilitySet,

    _unitTest_customizationObjectPathSetAsModified,
    // _unitTest_backendValidationErrorAdded,

} = imageSlice.actions;

const localSelectors = imageSlice.getSelectors();
export const localImageSelectors = localSelectors;

export const { getInitialState, selectors: imageSelectors } = imageSlice;

export default imageSlice.reducer;
